import {
  useGoalAvailableHoldings,
  useSyncGoalHoldings,
} from "creso-core/hooks";
import { imageAssetUrl } from "@/utils/url";
import {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Checkbox,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  V2Label,
} from "web-design-system";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { ChevronDown } from "lucide-react";
import Amount from "@funds/components/atoms/amount";
import { Goal, GoalHolding } from "creso-core/types";

export function LinkHoldings(props: {
  goal: Goal;
  onChange?: (val: number) => void;
  ref: Ref<{ linkHoldings: () => Promise<void> }>;
}) {
  const { goal, ref, onChange } = props;

  const { data: holdings } = useGoalAvailableHoldings(
    {
      id: goal?.id as number,
    },
    { enabled: !!goal?.id }
  );

  const { mutateAsync: syncGoalHoldings } = useSyncGoalHoldings();

  const holdingsLinkedToOtherGoals = useMemo(
    () =>
      holdings?.filter(
        (h) => h.linked_to_goal_id && h.linked_to_goal_id !== goal?.id
      ) ?? ([] as GoalHolding[]),
    [holdings, goal?.id]
  );

  const availableHoldings = useMemo(
    () =>
      holdings?.filter(
        (h) => !h.linked_to_goal_id || h.linked_to_goal_id === goal?.id
      ) ?? ([] as GoalHolding[]),
    [holdings, goal?.id]
  );

  const [linkedHoldings, setLinkedHoldings] = useState<string[]>([]);

  const updateLinkedHoldings = useCallback((holdings: string[]) => {
    setLinkedHoldings((f) => {
      const newHoldings = [...f];
      holdings.forEach((id) => {
        if (newHoldings.includes(id)) {
          newHoldings.splice(newHoldings.indexOf(id), 1);
        } else {
          newHoldings.push(id);
        }
      });
      return newHoldings;
    });
  }, []);

  useEffect(() => {
    if (holdings) {
      setLinkedHoldings(
        holdings
          .filter((f) => f.linked_to_goal_id === goal?.id)
          .map((f) => `${f.scheme_isin}_${f.folio_id}`)
      );
    }
  }, [holdings, goal?.id]);

  useEffect(() => {
    onChange?.(
      linkedHoldings
        .flatMap((lh) => {
          const [scheme_isin, folio_id] = lh.split("_");
          return holdings?.find(
            (h) =>
              h.scheme_isin === scheme_isin && h.folio_id === Number(folio_id)
          );
        })
        ?.reduce((acc, i) => acc + (i?.rupee_balance ?? 0), 0) ?? 0
    );
  }, [linkedHoldings, holdings, onChange]);

  const groupedByScheme = useCallback((holdings: GoalHolding[]) => {
    if (!holdings) return [];

    const uniqueSchemeIsin = Array.from(
      new Set(holdings.map((a) => a.scheme_isin))
    );

    return uniqueSchemeIsin.map((isin) => {
      const folios = holdings?.filter((ah) => ah.scheme_isin === isin);
      return {
        name: folios[0]?.scheme_name,
        isin: folios[0]?.scheme_isin,
        total: folios?.reduce((acc, f) => acc + f.rupee_balance, 0),
        folios,
      };
    });
  }, []);

  const getCheckedState = (holdings: string[]) => {
    if (holdings.every((h) => linkedHoldings.includes(h))) return true;
    if (holdings.some((f) => linkedHoldings.includes(f)))
      return "indeterminate";
    return false;
  };

  useImperativeHandle(ref, () => ({
    linkHoldings: async () => {
      await syncGoalHoldings({
        id: goal?.id as number,
        holdings: linkedHoldings.map((h) => {
          const [scheme_isin, folio_id] = h.split("_");
          return { scheme_isin, folio_id: Number(folio_id) };
        }),
      });
    },
  }));

  const renderHoldingsTree = (holdings: GoalHolding[], disabled = false) => {
    const holdingsGroupedByScheme = groupedByScheme(holdings);

    const sortedHoldings =
      holdingsGroupedByScheme.toSorted?.((a, b) => {
        if (a.folios.some((f) => f.linked_to_goal_id === goal?.id)) return -1;
        if (b.folios.some((f) => f.linked_to_goal_id === goal?.id)) return 1;
        return 0;
      }) || holdingsGroupedByScheme;

    return sortedHoldings.map((gs) => {
      return (
        <div
          key={gs.isin}
          className="flex flex-col p-4 border border-border rounded-md"
        >
          <div className="flex flex-row gap-2">
            {!disabled && (
              <Checkbox
                className="mt-0.25"
                disabled={disabled}
                id={gs.isin}
                onCheckedChange={() =>
                  updateLinkedHoldings(
                    gs.folios.map((f) => `${gs.isin}_${f.folio_id}`)
                  )
                }
                checked={getCheckedState(
                  gs.folios.map((f) => `${gs.isin}_${f.folio_id}`)
                )}
              />
            )}
            <V2Label
              htmlFor={gs.isin}
              className={cn(
                "text-sm font-normal",
                disabled && "text-muted-foreground"
              )}
            >
              {gs.name}
            </V2Label>
            <span className="ml-auto text-sm">
              <Amount precision={0}>{gs.total}</Amount>
            </span>
          </div>
          <ConditionalRenderer condition={gs.folios.length > 1}>
            <div className="flex flex-col gap-2 mt-2 ml-4">
              {gs.folios.map((f) => (
                <div key={f.folio_number} className="flex flex-row gap-2">
                  {!disabled && (
                    <Checkbox
                      className="mt-0.25"
                      disabled={disabled}
                      id={f.folio_number}
                      onCheckedChange={() =>
                        updateLinkedHoldings([`${gs.isin}_${f.folio_id}`])
                      }
                      checked={
                        !!linkedHoldings.includes(`${gs.isin}_${f.folio_id}`)
                      }
                    />
                  )}
                  <V2Label
                    htmlFor={f.folio_number}
                    className={cn(
                      "text-sm font-normal",
                      disabled && "text-muted-foreground"
                    )}
                  >
                    Folio: {f.folio_number}
                  </V2Label>
                  <span className="ml-auto text-sm">
                    <Amount precision={0}>{f.rupee_balance}</Amount>
                  </span>
                </div>
              ))}
            </div>
          </ConditionalRenderer>
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col flex-1 gap-3 pb-5">
      <ConditionalRenderer condition={!!holdings?.length}>
        {renderHoldingsTree(availableHoldings, false)}
        <ConditionalRenderer condition={holdingsLinkedToOtherGoals.length > 0}>
          <Collapsible className="mt-5" defaultOpen>
            <CollapsibleTrigger className="text-sm w-full group font-semibold flex items-center justify-between">
              Holdings linked to other goals
              <ChevronDown className="group-data-[state=open]:rotate-180 size-4" />
            </CollapsibleTrigger>
            <CollapsibleContent className="gap-3 mt-2 flex flex-col">
              {renderHoldingsTree(holdingsLinkedToOtherGoals, true)}
            </CollapsibleContent>
          </Collapsible>
        </ConditionalRenderer>
      </ConditionalRenderer>
      <ConditionalRenderer condition={!holdings?.length}>
        <div className="flex flex-1 flex-col items-center justify-center">
          <img className="size-40" src={imageAssetUrl("empty.png")} />
          <span className="text-sm text-muted-foreground">
            No holdings found
          </span>
        </div>
      </ConditionalRenderer>
    </div>
  );
}
